import { Button } from '@material-ui/core';
import { getErrorMsg } from 'app/core/errors/error-utils';
import { PageNotFoundError } from 'app/core/errors/error-variants';
import { persistConfig } from 'app/core/redux/configure-store';
import { Markup } from 'interweave';
import React from 'react';
import { purgeStoredState } from 'redux-persist';
import styled from 'styled-components/macro';

// NOTE: formatting is hardcoded here to avoid dependency on any
// theme providers or other providers.
const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  > * {
    text-align: center;
  }

  color: rgba(0, 0, 0, 0.8);
  font-size: 0.875rem;
  font-family: Poppins, Arial, sans-serif, 'Apple Color Emoji',
    color-emoji, 'Segoe UI Emoji', 'Segoe UI Symbol';
  font-weight: 500;
  line-height: 1.43;
`;

const StyledButton = styled(Button)`
  margin: 20px 40px 0;
  border-radius: 10px;
  align-self: stretch;
  font-family: Poppins, Arial, sans-serif;
  text-transform: none;
  box-shadow: none;
  padding: 10px 40px;
  font-size: 16px;
  font-weight: 500;
  background: #31ad31;
  color: white;
`;

const ErrorText = styled.p`
  align-self: stretch;
  color: #f6645b;
  border: 1px double #f6645b80;
  border-radius: 3px;
  background: #f6645b10;
  margin-top: 20px;
  padding: 10px 14px;
  text-align: start;
`;

const AfterMessage = styled.div`
  margin-top: 10px;
  color: grey;
  font-size: 12px;
`;

const PadCollapse: React.VFC<{
  pad: number;
  proportion?: number;
}> = ({ pad, proportion = 1 }) => (
  <div
    css={`
      flex: ${proportion * 10000} 0 0;
      max-height: ${pad * 10}px;
    `}
  />
);

type Props = {
  error: Error;
  resetError?: () => unknown;
};

export default function ErrorScreen({
  error,
  resetError,
}: Props): JSX.Element {
  return (
    <Container>
      <PadCollapse pad={4} />
      {error instanceof PageNotFoundError ? (
        <>
          <h2>404. That's an error.</h2>
          <p>
            Oops! Looks like we couldn't find that page. Try heading
            to the home page and looking from there.
          </p>
          <StyledButton variant="contained" href="/" disableElevation>
            Go to home page
          </StyledButton>
        </>
      ) : (
        <>
          <h2>Yikes. That's an error.</h2>
          <p>
            This app is still in development, so please bear with us
            as we fix any bugs that come up!
          </p>
          <ErrorText>
            <Markup noWrap content={getErrorMsg(error)} />
          </ErrorText>
          <StyledButton
            disableElevation
            variant="contained"
            onClick={() => {
              resetError?.();
              window.location.reload();
            }}
          >
            Reload page
          </StyledButton>
          <StyledButton
            disableElevation
            variant="contained"
            onClick={() => {
              resetError?.();
              purgeStoredState(persistConfig);
              window.localStorage.clear();
              window.location.href = '/';
            }}
          >
            Reset app
          </StyledButton>
          <PadCollapse pad={3} />
          <AfterMessage>
            <p>
              If the error persists, please get in touch with us at{' '}
              <i>
                <a href="mailto:support@iwarranty.co">
                  support@iwarranty.co
                </a>
              </i>{' '}
              before giving us a bad rating - this is a brand new app,
              and we will do our very best to help resolve the issue.
            </p>
          </AfterMessage>
        </>
      )}
      <PadCollapse pad={6} />
    </Container>
  );
}
